import { apiClient } from "api/trpc/trpc-client"
import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import Loader from "reusable/mobx-helpers/Loader"
import ModalState from "reusable/mobx-helpers/ModalState"
import { AdvisorClientContactDto } from "@gap/backend/app-services/advisor/client/contact/models/AdvisorClientContactDto"
import { DocShareDto } from "@gap/backend/app-services/doc-share/models/DocShareDto"
import { getDocumentNameForWorkspaceDocument } from "@gap/utils/utils"
import { ShareDocumentDialogProps } from "./ShareDocumentDialog"
import { DocDto } from "@gap/backend/app-services/doc/models/DocDto"

interface ShareDocumentStoreProps {
   document: DocDto.outputDoc | DocDto.entity | DocShareDto.BusinessPlanDocument
   clientOrgId: string
}

export default class ShareDocumentStore {
   sp
   loader = new Loader(true)
   modalState = new ModalState()

   advisorClientId: string | undefined
   clientContacts: AdvisorClientContactDto.minimal[] = []
   clientXeroId?: string
   clientName = ''
   documentAccessRights: DocShareDto.DocumentAccessRights[] = []

   constructor(sp: ShareDocumentStoreProps) {
      this.sp = sp
   }

   get shareDocumentDialogProps(): ShareDocumentDialogProps | null {
      if (!this.advisorClientId || !this.document) {
         return null
      }

      return {
         client: {
            advisorClientId: this.advisorClientId,
            contacts: this.clientContacts,
            orgId: this.sp.clientOrgId,
            xeroId: this.clientXeroId,
            name: this.clientName
         },
         documentAccessRights: this.documentAccessRights,
         modalState: this.modalState,
         document: this.document
      }
   }

   get documentClientName() {
      if ('type' in this.sp.document) {
         return '' // Not needed for business plan documents
      }

      return getDocumentNameForWorkspaceDocument({
         clientName: this.clientName,
         docTypeName: this.sp.document.docTypeName,
         finalisedAt: this.sp.document.lastUpdatedAt
      })
   }

   get document(): DocShareDto.Document {
      if ('type' in this.sp.document) {
         return this.sp.document
      }
      return {
         type: 'workspace-document',
         documentId: this.sp.document.id,
         workItemId: this.sp.document.workItemId,
         docTypeName: this.sp.document.docTypeName,
         name: this.documentClientName
      }
   }

   fetch = async () => {
      await this.loader.executeLoading(async () => {
         // Fetch client details and document access rights in parallel
         const [advisorClientId, clientDetails, documentAccessRights] = await Promise.all([
            apiClient.advisor.getClientIdFromClientOrgId.query({ clientOrgId: this.sp.clientOrgId }),
            apiClient.advisor.getMyBasicContactsForClient.query({ clientOrgId: this.sp.clientOrgId }),
            apiClient.docShare.getDocumentAccessRights.query({
               clientOrgId: this.sp.clientOrgId,
               documents: [this.document]
            })
         ])

         this.advisorClientId = advisorClientId
         this.clientContacts = clientDetails.contacts
         this.clientXeroId = clientDetails.xeroId
         this.clientName = clientDetails.clientName
         this.document.name = this.documentClientName
         this.documentAccessRights = documentAccessRights
      })
   }
}

export const { Provider: ShareDocumentStoreProvider, useProvidedStore: useShareDocumentStore } = createStoreContext(ShareDocumentStore)
